import { makeAutoObservable, runInAction } from "mobx";

import { Tastes } from "@/modules/mocksData";

import type { DishFormType } from "../BrandFormMenu/types";

export type BranchAvailability = {
  branchId: string;
  branchName: string;
  city?: string;
  available: boolean;
};

export type DishDrawerMode = "create" | "edit";

export const defaultDishForm: DishFormType = {
  dishNameEnglish: "",
  dishNameArabic: "",
  descriptionEnglish: "",
  descriptionArabic: "",
  preparationTime: "",
  price: "",
  active: true,
  unavailableBranchIds: [],
  flagsAndStatuses: [],
  meal: [],
  occasion: [],
  ratings: Tastes,
  dietTags: [],
  allergenTags: [],
  calories: "",
  burnCaloriesTime: "",
  protein: "",
  fiber: "",
  totalFat: "",
  totalSugar: "",
  saturatedFat: "",
  addedSugars: "",
  transFat: "",
  salt: "",
  cholesterol: "",
  sodium: "",
  carbohydrate: "",
  caffeine: "",
  saltContent: false,
  mediaItems: [],
  images: { media: [] },
};

export const DISH_STEPS = [
  { id: 1, label: "Details" },
  { id: 2, label: "Meal & taste" },
  { id: 3, label: "Diet & nutrition" },
  { id: 4, label: "Photos" },
  { id: 5, label: "Availability" },
] as const;

export type StepId = (typeof DISH_STEPS)[number]["id"];

export class DishDrawerStore {
  isOpen = false;
  currentStep: StepId = 1;
  mode: DishDrawerMode = "create";
  categoryKey: string | null = null;
  dish: DishFormType = { ...defaultDishForm };
  branchAvailability: BranchAvailability[] = [];
  isDirty = false;
  showDiscardConfirm = false;
  showDeleteConfirm = false;

  constructor() {
    makeAutoObservable(this);
  }

  openForCreate(
    categoryKey: string,
    branches: Omit<BranchAvailability, "available">[]
  ) {
    runInAction(() => {
      this.categoryKey = categoryKey;
      this.mode = "create";
      this.dish = { ...defaultDishForm };
      this.currentStep = 1;
      this.isDirty = false;
      // New dish: all branches available by default
      this.branchAvailability = branches.map(b => ({ ...b, available: true }));
      this.isOpen = true;
    });
  }

  openForEdit(
    categoryKey: string,
    dish: DishFormType,
    branches: Omit<BranchAvailability, "available">[],
    savedBranchAvailability?: BranchAvailability[]
  ) {
    runInAction(() => {
      this.categoryKey = categoryKey;
      this.mode = "edit";
      this.dish = { ...dish };
      this.currentStep = 1;
      this.isDirty = false;
      this.isOpen = true;

      if (savedBranchAvailability && savedBranchAvailability.length > 0) {
        const savedMap = new Map(
          savedBranchAvailability.map(b => [b.branchId, b])
        );
        this.branchAvailability = branches.map(b => {
          const saved = savedMap.get(b.branchId);
          return saved
            ? { ...b, available: saved.available }
            : { ...b, available: false };
        });
      } else if (dish.unavailableBranchIds) {
        const unavailableSet = new Set(dish.unavailableBranchIds);
        this.branchAvailability = branches.map(b => ({
          ...b,
          available: !unavailableSet.has(b.branchId),
        }));
      } else {
        // No availability data — default all branches to available
        this.branchAvailability = branches.map(b => ({
          ...b,
          available: true,
        }));
      }
    });
  }

  close() {
    if (this.isDirty) {
      this.showDiscardConfirm = true;
    } else {
      this._forceClose();
    }
  }

  _forceClose() {
    runInAction(() => {
      this.isOpen = false;
      this.showDiscardConfirm = false;
      this.showDeleteConfirm = false;
      this.isDirty = false;
      this.currentStep = 1;
      this.dish = { ...defaultDishForm };
      this.branchAvailability = [];
      this.categoryKey = null;
    });
  }

  cancelDiscard() {
    this.showDiscardConfirm = false;
  }

  confirmDiscard() {
    this._forceClose();
  }

  requestDelete() {
    this.showDeleteConfirm = true;
  }

  cancelDelete() {
    this.showDeleteConfirm = false;
  }

  goToStep(step: StepId) {
    this.currentStep = step;
  }

  nextStep() {
    if (this.currentStep < 5) {
      this.currentStep = (this.currentStep + 1) as StepId;
    }
  }

  prevStep() {
    if (this.currentStep > 1) {
      this.currentStep = (this.currentStep - 1) as StepId;
    }
  }

  setDishField<K extends keyof DishFormType>(key: K, value: DishFormType[K]) {
    runInAction(() => {
      this.dish = { ...this.dish, [key]: value };
      this.isDirty = true;
    });
  }

  setDish(dish: Partial<DishFormType>) {
    runInAction(() => {
      this.dish = { ...this.dish, ...dish };
      this.isDirty = true;
    });
  }

  toggleMasterAvailability(value: boolean) {
    runInAction(() => {
      this.dish = { ...this.dish, active: value };
      this.isDirty = true;
    });
  }

  toggleBranchAvailability(branchId: string, value: boolean) {
    runInAction(() => {
      this.branchAvailability = this.branchAvailability.map(b =>
        b.branchId === branchId ? { ...b, available: value } : b
      );
      this.isDirty = true;
    });
  }

  toggleAllBranches(value: boolean) {
    runInAction(() => {
      this.branchAvailability = this.branchAvailability.map(b => ({
        ...b,
        available: value,
      }));
      this.isDirty = true;
    });
  }

  get allBranchesAvailable() {
    return (
      this.branchAvailability.length > 0 &&
      this.branchAvailability.every(b => b.available)
    );
  }

  get availableBranchCount() {
    return this.branchAvailability.filter(b => b.available).length;
  }

  get enabledBranchIds(): string[] {
    return this.branchAvailability
      .filter(b => b.available)
      .map(b => b.branchId);
  }

  getBranchScopeLabel(totalBranches: number): string {
    if (this.branchAvailability.length === 0) {
      return totalBranches > 0 ? "All branches" : "No branches";
    }
    const availableCount = this.availableBranchCount;
    if (availableCount === 0) return "Not available";
    if (availableCount === totalBranches) return "All branches";
    return `${availableCount} branch${availableCount > 1 ? "es" : ""}`;
  }

  getPayload() {
    return {
      dish: { ...this.dish },
      branchAvailability: [...this.branchAvailability],
    };
  }

  markClean() {
    this.isDirty = false;
  }
}
